import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { cn } from "../lib/utils";
import api from "../lib/api";
import {
  Users,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  Link2Off,
  Clock,
  DollarSign,
} from "lucide-react";

/**
 * Team capacity & utilization + per-employee KPI, driven by the Worksection
 * mirror (`GET /worksection/team-utilization`). Shared between the owner
 * dashboard (DashboardV2) and the standalone /team-utilization page for the
 * PM lead.
 *
 * KPI model (see backend docstring): value = task client price amortized by
 * the member's share of the task's total salary cost (hours × rate); profit =
 * value − salary cost of the logged hours, counted over priced tasks only.
 */

const fmt = (n) => `$${Math.round(n || 0).toLocaleString("en-US")}`;

const FLAG_LABELS = {
  not_linked_to_staff: { label: "WS user not linked to staff", tone: "red" },
  no_ws_time: { label: "No time in Worksection", tone: "red" },
  low_linking: { label: "<50% hours linked to tasks", tone: "amber" },
  no_rate: { label: "No salary/rate set", tone: "amber" },
  tasks_without_price: { label: "Tasks w/o client price", tone: "amber" },
};

const FlagChip = ({ flag }) => {
  const f = FLAG_LABELS[flag];
  if (!f) return null;
  const cls =
    f.tone === "red"
      ? "bg-red-500/10 text-red-400 border-red-500/20"
      : "bg-amber-500/10 text-amber-400 border-amber-500/20";
  return (
    <span className={cn("inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] leading-none", cls)}>
      <AlertTriangle className="h-2.5 w-2.5" />
      {f.label}
    </span>
  );
};

const MemberCard = ({ m, plannedHours }) => {
  const [open, setOpen] = useState(false);
  const util = m.utilization_pct || 0;
  const utilColor = util >= 100 ? "text-red-400" : util >= 70 ? "text-emerald-400" : "text-yellow-400";
  const profit = m.kpi_profit_usd || 0;
  const profitColor = profit > 0 ? "text-emerald-400" : profit < 0 ? "text-red-400" : "text-zinc-400";
  const unscored = m.unscored_cost_usd || 0;
  const pct = (h) => `${Math.min(100, (h / plannedHours) * 100)}%`;
  const hasDetails =
    (m.projects?.length || 0) + (m.project_only?.length || 0) + (m.unlinked_ws?.length || 0) > 0;

  return (
    <div className="rounded-md border border-zinc-800 bg-zinc-950/40 p-3">
      {/* header */}
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm text-zinc-50 font-medium truncate">{m.name}</p>
          {m.position && <p className="text-[11px] text-zinc-500 truncate">{m.position}</p>}
        </div>
        <div className="text-right shrink-0">
          <span className={cn("text-sm font-mono font-bold", utilColor)}>{util}%</span>
          <p className="text-[10px] text-zinc-500">{m.total_hours}h / {plannedHours}h</p>
        </div>
      </div>

      {/* stacked bar: linked / project-only / unlinked WS / manual */}
      <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden flex mt-2">
        <div className="h-full bg-emerald-500" style={{ width: pct(m.ws_task_linked_hours) }} />
        <div className="h-full bg-sky-500" style={{ width: pct(m.ws_project_only_hours) }} />
        <div className="h-full bg-amber-500" style={{ width: pct(m.ws_unlinked_hours) }} />
        <div className="h-full bg-emerald-300" style={{ width: pct(m.manual_hours) }} />
      </div>
      <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1.5 text-[10px] text-zinc-500 font-mono">
        <span><span className="text-emerald-400">●</span> {m.ws_task_linked_hours}h tasks</span>
        {m.ws_project_only_hours > 0 && <span><span className="text-sky-400">●</span> {m.ws_project_only_hours}h project-only</span>}
        {m.ws_unlinked_hours > 0 && <span><span className="text-amber-400">●</span> {m.ws_unlinked_hours}h not linked</span>}
        {m.manual_hours > 0 && <span><span className="text-emerald-300">●</span> {m.manual_hours}h manual</span>}
      </div>

      {/* KPI line — profit only over scorable (priced) work; unscored cost is a
          data gap shown separately, never a minus */}
      <div className="flex items-center justify-between mt-2 pt-2 border-t border-zinc-800/70">
        {m.scorable ? (
          <div className="flex gap-3 text-[11px] font-mono">
            <span className="text-zinc-400" title="Attributed client value">⇡ {fmt(m.value_usd)}</span>
            <span className="text-zinc-500" title="Salary cost of hours on priced tasks">⇣ {fmt(m.cost_priced_usd)}</span>
            <span className={cn("font-bold", profitColor)} title="KPI: value − cost of priced work">
              {profit >= 0 ? "+" : ""}{fmt(profit)}
            </span>
          </div>
        ) : (
          <span className="inline-flex items-center gap-1 text-[11px] text-amber-400/90" title="No hours on tasks with a client price — fill task prices / link WS tasks to score this member">
            <AlertTriangle className="h-3 w-3" />
            Can't score — no priced tasks
          </span>
        )}
        {m.kpi_ratio !== null && m.kpi_ratio !== undefined && (
          <span className={cn(
            "text-[10px] font-mono px-1.5 py-0.5 rounded border",
            m.kpi_ratio >= 2 ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20"
              : m.kpi_ratio >= 1 ? "bg-yellow-500/10 text-yellow-400 border-yellow-500/20"
              : "bg-red-500/10 text-red-400 border-red-500/20"
          )} title="Value / cost multiplier (priced work only)">
            ×{m.kpi_ratio}
          </span>
        )}
      </div>
      {unscored > 0 && (
        <p className="text-[10px] text-zinc-500 mt-1" title="Salary cost of hours that can't be evaluated: tasks without client price + hours not linked to portal tasks">
          <span className="text-amber-500/80">!</span> {m.unscored_hours}h · {fmt(unscored)} not scorable (no price / not linked)
        </p>
      )}

      {/* flags */}
      {m.flags?.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {m.flags.map((f) => <FlagChip key={f} flag={f} />)}
        </div>
      )}

      {/* expandable breakdown */}
      {hasDetails && (
        <>
          <button
            onClick={() => setOpen(!open)}
            className="w-full flex items-center justify-center gap-1 text-[10px] text-zinc-500 hover:text-zinc-300 mt-2 pt-1"
          >
            {open ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            {open ? "Hide breakdown" : `Breakdown · ${m.projects?.length || 0} project${(m.projects?.length || 0) === 1 ? "" : "s"}`}
          </button>
          {open && (
            <div className="mt-1 space-y-2">
              {m.projects?.map((p) => (
                <div key={p.project_id || p.name} className="rounded border border-zinc-800/70 bg-zinc-900/40 px-2 py-1.5">
                  <div className="flex items-center justify-between text-[11px]">
                    {p.project_id ? (
                      <Link to={`/projects/${p.project_id}`} className="text-zinc-300 hover:text-zinc-50 truncate font-medium">{p.name}</Link>
                    ) : (
                      <span className="text-zinc-300 truncate font-medium">{p.name}</span>
                    )}
                    {p.profit_usd === null || p.profit_usd === undefined ? (
                      <span className="font-mono shrink-0 ml-2 text-[10px] text-amber-400/90" title="No client price on tasks — can't evaluate">⚠ no price</span>
                    ) : (
                      <span className={cn("font-mono shrink-0 ml-2", p.profit_usd >= 0 ? "text-emerald-400" : "text-red-400")}>
                        {p.profit_usd >= 0 ? "+" : ""}{fmt(p.profit_usd)}
                      </span>
                    )}
                  </div>
                  <p className="text-[10px] text-zinc-500 font-mono">
                    {p.hours}h · cost {fmt(p.cost_usd)} · value {fmt(p.value_usd)}
                    {p.unpriced_hours > 0 && <span className="text-amber-500/70"> · {p.unpriced_hours}h unpriced</span>}
                  </p>
                  {p.tasks?.map((t) => (
                    <div key={t.task_id} className="flex items-center justify-between text-[10px] text-zinc-500 pl-2 mt-0.5">
                      <Link to={`/tasks/${t.task_id}`} className="truncate hover:text-zinc-300">
                        {t.name}
                        {t.source === "manual" && <span className="text-zinc-600"> · manual</span>}
                        {t.no_price && <span className="text-amber-500/80"> · no price</span>}
                      </Link>
                      <span className="font-mono shrink-0 ml-2">
                        {t.hours}h · {t.no_price ? <span className="text-amber-500/80" title="Client price not set on this task">price not set</span> : <>{fmt(t.value_usd)} of {fmt(t.price_usd)}</>}
                      </span>
                    </div>
                  ))}
                </div>
              ))}
              {m.project_only?.map((p) => (
                <div key={`po-${p.project_id}`} className="flex items-center justify-between rounded border border-sky-500/20 bg-sky-500/5 px-2 py-1.5 text-[11px]">
                  <span className="text-sky-300/90 truncate">{p.name} <span className="text-sky-500/60">· task not linked</span></span>
                  <span className="font-mono text-zinc-400 shrink-0 ml-2">{p.hours}h · {fmt(p.cost_usd)}</span>
                </div>
              ))}
              {m.unlinked_ws?.map((p) => (
                <div key={`ul-${p.ws_project_id}`} className="flex items-center justify-between rounded border border-amber-500/20 bg-amber-500/5 px-2 py-1.5 text-[11px]">
                  <span className="text-amber-300/90 truncate flex items-center gap-1">
                    <Link2Off className="h-3 w-3 shrink-0" />{p.name} <span className="text-amber-500/60">· WS only</span>
                  </span>
                  <span className="font-mono text-zinc-400 shrink-0 ml-2">{p.hours}h · {fmt(p.cost_usd)}</span>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

const TeamUtilizationSection = ({ year, month, title = "Team capacity & utilization" }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const periodKey = `${year}-${String(month).padStart(2, "0")}`;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .get("/worksection/team-utilization", { params: { period: periodKey } })
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch((e) => { if (!cancelled) setError(e?.response?.data?.detail || e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [periodKey]);

  const totals = data?.totals;
  const linkedPct = totals?.ws_hours > 0 ? Math.round((totals.linked_hours / (totals.ws_hours + 0.0001)) * 100) : 0;

  return (
    <Card className="bg-zinc-900/50 border-zinc-800">
      <CardHeader className="pb-2 flex flex-row items-center justify-between">
        <CardTitle className="text-base font-semibold text-zinc-50 font-['Manrope'] flex items-center gap-2">
          <Users className="h-4 w-4 text-sky-400" />
          {title} · {periodKey}
        </CardTitle>
        {totals && (
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] font-mono text-zinc-400">
            <span className="flex items-center gap-1"><Clock className="h-3 w-3 text-zinc-500" />{totals.ws_hours}h WS · {linkedPct}% linked</span>
            <span className="flex items-center gap-1" title="Cost of scored (priced) work · attributed client value"><DollarSign className="h-3 w-3 text-zinc-500" />cost {fmt(totals.cost_priced_usd)} · value {fmt(totals.value_usd)}</span>
            <span className={cn("font-bold", totals.profit_usd >= 0 ? "text-emerald-400" : "text-red-400")} title="KPI profit over scored work">
              {totals.profit_usd >= 0 ? "+" : ""}{fmt(totals.profit_usd)}
            </span>
            {totals.unscored_cost_usd > 0 && (
              <span className="text-amber-400/80" title="Salary cost that can't be evaluated — tasks without client price or hours not linked">
                ! {fmt(totals.unscored_cost_usd)} unscored
              </span>
            )}
          </div>
        )}
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-sm text-zinc-500 text-center py-6">Loading…</p>
        ) : error ? (
          <p className="text-sm text-red-400 text-center py-6">{error}</p>
        ) : !data?.members?.length ? (
          <p className="text-sm text-zinc-500 text-center py-6">No time logged in this period (Worksection + portal)</p>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
              {data.members.map((m) => (
                <MemberCard key={m.key} m={m} plannedHours={data.planned_hours} />
              ))}
            </div>
            <p className="text-[10px] text-zinc-600 mt-3">
              Hours from Worksection mirror (manual portal hours used only for tasks without WS links — never double-counted).
              Value = task client price × member share of the task's total salary cost (hours × rate — a senior hour weighs its real cost). KPI profit = value − salary cost (salary / 160h),
              counted only over hours on priced tasks; hours without a price or without links are marked "not scorable" instead of a minus.
              Amber rows = logged time not linked to portal projects — fix links on the project page or in{" "}
              <Link to="/settings/worksection/users" className="text-zinc-500 underline hover:text-zinc-300">Worksection users</Link>.
            </p>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default TeamUtilizationSection;
